import { useState } from 'preact/hooks';
import type { RatedProductWire, RichTextDoc } from 'shared';
import { roundCents } from 'shared';
import { cart, findRatedTier, reclampPayment } from '@/state/session.js';
import { tierSpecLabels, type TierSpecLabels } from '@/lib/formatters.js';
import {
  carrySurchargeSelections,
  defaultSurchargeSelections,
  selectedSurchargeSum,
} from '@/lib/surcharges.js';
import type { CartEntry, CartSurcharge } from '@/lib/widget-types.js';

function findCartLookup(
  cartEntries: CartEntry[],
  terms: RatedProductWire[],
): { cartIndex: number; termIndex: number } | null {
  for (let i = 0; i < cartEntries.length; i++) {
    const entry = cartEntries[i];
    if (entry === undefined) continue;
    const termIndex = terms.findIndex((t) => t.quote_item_id === entry.quoteItemId);
    if (termIndex !== -1) return { cartIndex: i, termIndex };
  }
  return null;
}

/** Extends `TierSpecLabels` rather than restating it, so a new spec label reaches the
 *  cards and the order summary together. */
interface ProductCardModel extends TierSpecLabels {
  termTiers: RatedProductWire[];
  currentTier: RatedProductWire;
  productName: string;
  productDescription: RichTextDoc;
  isInCart: boolean;
  activeSurcharges: CartSurcharge[];
  /** Pre-tax: sale price plus selected surcharges. Tax is applied at cart level. */
  currentSubtotal: number;
  /** The cheapest term's subtotal — the cards' "From" headline before a term is picked. */
  lowestSubtotal: number | null;
  /** Any of `termTiers` priced the way `currentSubtotal` is. */
  subtotalFor: (tier: RatedProductWire) => number;
  getSurchargeSelected: (code: string) => boolean;
  toggleSurcharge: (code: string) => void;
  handleTermChange: (months: number) => void;
  /** Adds the product and returns its cart index, or null when it is already in the cart. */
  handleSelectPlan: () => number | null;
  handleRemove: () => void;
}

/** Null when none of the pinned tier ids still resolve against the current rating. */
export function useProductCard(quoteItemIds: string[]): ProductCardModel | null {
  const [localTermIndex, setLocalTermIndex] = useState(0);
  const [localSurcharges, setLocalSurcharges] = useState<CartSurcharge[]>(() => {
    const firstId = quoteItemIds[0];
    const firstTier = firstId === undefined ? undefined : findRatedTier(firstId);
    return firstTier !== undefined ? defaultSurchargeSelections(firstTier) : [];
  });

  const termTiers = quoteItemIds.flatMap((id) => {
    const tier = findRatedTier(id);
    return tier === undefined ? [] : [tier];
  });

  const cartLookup = findCartLookup(cart.value, termTiers);
  const isInCart = cartLookup !== null;

  const currentTermIndex = cartLookup?.termIndex ?? localTermIndex;
  const resolvedTier = termTiers[currentTermIndex] ?? termTiers[0];
  if (resolvedTier === undefined) return null;
  // Re-declared with an explicit type rather than relying on the narrowing above: the handlers
  // below are hoisted `function` declarations, and TS discards narrowing inside those.
  const currentTier: RatedProductWire = resolvedTier;

  const cartEntry = cartLookup === null ? undefined : cart.value[cartLookup.cartIndex];
  const activeSurcharges = cartEntry?.surcharges ?? localSurcharges;

  // Pay-in-full until this product has a payment choice of its own — the tiers screen comes
  // before the payment step, so quoting the finance price there would misprice the headline.
  const paymentType = cartEntry?.payment.type ?? 'pay_in_full';
  const salePriceFor = (tier: RatedProductWire): number =>
    paymentType === 'finance' ? tier.sale_price_finance : tier.sale_price_pif;

  // Rounded per component the same way `state/pricing.ts` rounds a cart line, so a
  // card's subtotal and that plan's line in the cart are the same number.
  const subtotalFor = (tier: RatedProductWire): number =>
    roundCents(salePriceFor(tier)) + roundCents(selectedSurchargeSum(tier, activeSurcharges));

  const currentSubtotal = subtotalFor(currentTier);
  const subtotalCandidates = termTiers.map(subtotalFor);
  const lowestSubtotal = subtotalCandidates.length > 0 ? Math.min(...subtotalCandidates) : null;

  function getSurchargeSelected(code: string): boolean {
    return activeSurcharges.find((s) => s.code === code)?.selected ?? false;
  }

  function toggleSurcharge(code: string) {
    if (cartLookup !== null && cartEntry !== undefined) {
      const next = [...cart.value];
      next[cartLookup.cartIndex] = {
        ...cartEntry,
        surcharges: cartEntry.surcharges.map((s) =>
          s.code === code ? { ...s, selected: !s.selected } : s,
        ),
      };
      cart.value = next;
      return;
    }
    setLocalSurcharges((prev) =>
      prev.map((s) => (s.code === code ? { ...s, selected: !s.selected } : s)),
    );
  }

  function handleTermChange(months: number) {
    const newIndex = termTiers.findIndex((t) => t.term_months === months);
    const newTier = termTiers[newIndex];
    if (newTier === undefined) return;

    const carried = carrySurchargeSelections(activeSurcharges, newTier);

    // Keep the local term in sync even while the plan sits in the cart, so removing
    // it doesn't snap the card back to the first term the customer never chose.
    setLocalTermIndex(newIndex);
    setLocalSurcharges(carried);

    if (cartLookup !== null && cartEntry !== undefined) {
      const next = [...cart.value];
      next[cartLookup.cartIndex] = {
        quoteItemId: newTier.quote_item_id,
        surcharges: carried,
        // A shorter coverage term allows a shorter loan and prices differently, so the
        // existing choice is refitted rather than carried over as-is.
        payment: reclampPayment(cartEntry.payment, newTier, carried),
      };
      cart.value = next;
    }
  }

  /** Returns the new entry's index — the caller sends the customer to its payment step. */
  function handleSelectPlan(): number | null {
    if (cartLookup !== null) return null;
    const entry: CartEntry = {
      quoteItemId: currentTier.quote_item_id,
      surcharges: activeSurcharges.map((s) => ({ ...s })),
      // Payment is chosen on the next step; this is the default it opens on.
      payment: { type: 'pay_in_full' },
    };
    cart.value = [...cart.value, entry];
    return cart.value.length - 1;
  }

  function handleRemove() {
    if (cartLookup === null) return;
    cart.value = cart.value.filter((_, i) => i !== cartLookup.cartIndex);
  }

  return {
    termTiers,
    currentTier,
    productName: currentTier.product_name,
    productDescription: currentTier.product_description,
    isInCart,
    activeSurcharges,
    currentSubtotal,
    lowestSubtotal,
    subtotalFor,
    ...tierSpecLabels(currentTier),
    getSurchargeSelected,
    toggleSurcharge,
    handleTermChange,
    handleSelectPlan,
    handleRemove,
  };
}
